import { Controller } from "@hotwired/stimulus";
import { Bubble } from "../lib/bubble";
import { t } from "../lib/i18n";

/**
 * Une bulle de BD qui POINTE un élément (lib/bubble, sorte `callout`) — pas un tooltip :
 * elle signale un état, et reste tant que son marqueur existe. Mais elle se ferme d'un tap,
 * sur sa croix ou sur ce qu'elle pointe — et ne revient pas tant qu'on n'a pas rechargé la
 * page : l'état existe toujours (elle reviendra à la prochaine ouverture), on l'a juste lu.
 * Ce « fermé » n'est retenu que pour la page, jamais sur le compte — c'est ce qui la
 * distingue d'une bulle « première fois » (contrôleur `coach`).
 *
 * L'élément du contrôleur n'est qu'un marqueur (rendu serveur, là où le morph le gère) : la
 * bulle vit dans le <body>. Le marqueur disparaît (morph, frame rechargé) → la bulle aussi,
 * sans compter comme fermée : si le marqueur revient, elle revient.
 *
 * Usage :
 *   <span hidden data-controller="callout"
 *         data-callout-anchor-value="dom_id_of_target"
 *         data-callout-text-value="L'image doit être personnalisée avant d'être publiée"
 *         data-callout-placement-value="top"></span>
 *
 * `data-callout-key-value` : ce qui identifie la bulle pour la page (par défaut son ancre).
 * `data-callout-duration-value` (ms) : une bulle de passage, qui s'efface aussi seule.
 */

/** Les bulles fermées depuis le chargement de la page. */
const dismissed = new Set();

export default class extends Controller {
  static values = {
    anchor: String,
    key: String,
    text: String,
    placement: { type: String, default: "top" },
    duration: Number,
  };

  connect() {
    if (dismissed.has(this.key)) return;
    const anchor = document.getElementById(this.anchorValue);
    if (!anchor) return;

    this.dismiss = () => {
      dismissed.add(this.key);
      this.disconnect();
    };
    this.bubble = new Bubble(anchor, {
      kind: "callout",
      text: this.textValue,
      placement: this.placementValue,
      closeLabel: t("common.close", "Fermer"),
      onDismiss: this.dismiss,
    });
    anchor.addEventListener("pointerdown", this.dismiss, { once: true });
    this.anchor = anchor;
    if (this.durationValue) this.timer = setTimeout(this.dismiss, this.durationValue);
  }

  disconnect() {
    clearTimeout(this.timer);
    this.anchor?.removeEventListener("pointerdown", this.dismiss);
    this.bubble?.dismiss();
    this.bubble = null;
  }

  get key() {
    return this.keyValue || this.anchorValue;
  }
}
